import React, { useEffect } from "react";

import { KSelect, KSelectAutocomplete } from "@kyburz/kdesign-system";
import { useDispatch, useSelector } from "react-redux";
import KSwitch from "../../../../components/ui/button/switch";
import useSetState from "../../../../hooks/useSetState";
import { getCustomers } from "../../../../apiCalls/auth";
import { setAlert } from "../../../../redux/actions/actions";
import { useStyles } from "./styles";

const INVOICE_TEMPLATES = [
  { value: "default_monthly", label: "Default Monthly" },
  { value: "default_quarterly", label: "Default Quarterly" },
  { value: "default_yearly", label: "Default Yearly" },
  { value: "default_group_monthly", label: "Default Group Monthly" },
  { value: "default_group_quarterly", label: "Default Group Quarterly" },
  { value: "parent_monthly", label: "Parent Monthly" },
  { value: "parent_quarterly", label: "Parent Quarterly" },
  { value: "parent_yearly", label: "Parent Yearly" },
  { value: "parent_group_monthly", label: "Parent Group Monthly" },
  { value: "parent_group_quarterly", label: "Parent Group Quarterly" },
  { value: "planzer", label: "Planzer" },
];

type ExportToSettingsProps = {
  internals: Record<string, any>;
  setInternals: (v: any) => void;
};

export const InternalBillingSettings = ({
  internals,
  setInternals,
}: ExportToSettingsProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const currentCustomer: string = useSelector(
    (state: any) => state?.customer?.currentCustomer?.shortname,
  );

  const [{ parentCompanies, childCompanies, loading }, setState] = useSetState({
    parentCompanies: [],
    childCompanies: [],
    loading: true,
  });

  useEffect(() => {
    getCustomers().then((res) => {
      setState({
        parentCompanies: res.data
          .filter(
            (el) =>
              el?.company_internals?.is_parent &&
              el.shortname !== currentCustomer,
          )
          .map((el) => ({ label: el.shortname, name: el.name })),
        childCompanies: res.data
          .filter(
            (el) =>
              el?.company_internals?.parent_company_id === currentCustomer,
          )
          .map((el) => ({ label: el.shortname, name: el.name })),
        loading: false,
      });
    });
  }, [currentCustomer]);

  const handleParentToggle = (event) => {
    if (
      (internals?.is_parent && childCompanies.length) ||
      (!internals?.is_parent && internals.parent_company_id)
    ) {
      dispatch(
        setAlert(
          "Remove parent-child relationship before changing the parent status",
          "error",
        ),
      );
      return;
    }
    setInternals({ ...internals, is_parent: event.target.checked });
  };

  const handleSelectParent = (name: string, value: { label: string }): void => {
    setInternals({ ...internals, [name]: value?.label });
  };

  const handleSelectInvoiceTemplate = (e: any): void => {
    setInternals({ ...internals, invoice_template: e?.value });
  };

  if (loading) return <div className={classes.loader}>Loading...</div>;

  return (
    <div>
      <div className={classes.parentChildSettings}>
        <KSwitch
          name="isParent"
          onChange={handleParentToggle}
          checked={internals?.is_parent}
          label="Is Parent"
        />
        {internals?.is_parent ? (
          <div>
            <p className={classes.childCompaniesLabel}>Child Companies</p>
            {childCompanies.length
              ? childCompanies.map((company) => company.name).join(", ")
              : "-"}
          </div>
        ) : (
          <KSelectAutocomplete
            name="parent_company_id"
            label="Parent"
            value={internals?.parent_company_id}
            placeholder="Select Parent"
            onChange={handleSelectParent}
            elements={parentCompanies}
            isClearable
          />
        )}
      </div>
      {internals?.is_parent ||
      (!internals.is_parent && !internals.parent_company_id) ? (
        <KSelect
          label="Invoice Template"
          value={
            INVOICE_TEMPLATES.find(
              ({ value }) => value === internals?.invoice_template,
            )?.label
          }
          onSelect={handleSelectInvoiceTemplate}
          elements={INVOICE_TEMPLATES}
          name="invoice_template"
        />
      ) : (
        <div className={classes.hint}>
          Invoiced as a part of the parent company
        </div>
      )}
    </div>
  );
};

export default InternalBillingSettings;
